import React from 'react';
import { Box, Stack, ButtonBase, Typography, Tooltip, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useSettingsContext } from '../../../../setting/SettingsContext';
import SectionLabel from './SectionLabel';

const OPTIONS = [
    {
        value: 'vertical',
        en: 'Vertical',
        ar: 'عمودي',
        // Wide left column + short right rows
        layout: { sideW: 16, sideRows: 3, top: false },
    },
    {
        value: 'mini',
        en: 'Mini',
        ar: 'مضغوط',
        // Narrow left column showing icon dots
        layout: { sideW: 8, sideRows: 3, top: false },
    },
    {
        value: 'horizontal',
        en: 'Horizontal',
        ar: 'أفقي',
        // Top bar + content below
        layout: { sideW: 0, sideRows: 0, top: true },
    },
];

const LayoutPreview = ({ option, primary, divider, surface }) => {
    const { sideW, sideRows, top } = option.layout;
    return (
        <Box
            sx={{
                width: '100%',
                height: 44,
                display: 'flex',
                flexDirection: top ? 'column' : 'row',
                gap: 0.5,
                borderRadius: 1,
                overflow: 'hidden',
                border: `1px solid ${divider}`,
                bgcolor: surface,
            }}
        >
            {top ? (
                <>
                    <Box
                        sx={{
                            height: 10,
                            bgcolor: primary,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 0.5,
                        }}
                    >
                        <Box sx={{ width: 4, height: 3, borderRadius: 0.5, bgcolor: '#fff' }} />
                        <Box sx={{ width: 4, height: 3, borderRadius: 0.5, bgcolor: 'rgba(255,255,255,0.6)' }} />
                        <Box sx={{ width: 4, height: 3, borderRadius: 0.5, bgcolor: 'rgba(255,255,255,0.6)' }} />
                    </Box>
                    <Box sx={{ flex: 1, p: 0.5 }}>
                        <Box sx={{ width: '60%', height: 3, borderRadius: 0.5, bgcolor: divider, mb: 0.5 }} />
                        <Box sx={{ width: '40%', height: 2, borderRadius: 0.5, bgcolor: divider }} />
                    </Box>
                </>
            ) : (
                <>
                    <Box
                        sx={{
                            width: sideW,
                            bgcolor: primary,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            p: 0.5,
                        }}
                    >
                        {Array.from({ length: sideRows }).map((_, i) => (
                            <Box
                                key={i}
                                sx={{
                                    width: sideW > 10 ? 8 : 3,
                                    height: 2,
                                    borderRadius: 0.5,
                                    bgcolor: i === 0 ? '#fff' : 'rgba(255,255,255,0.6)',
                                }}
                            />
                        ))}
                    </Box>
                    <Box sx={{ flex: 1, p: 0.5 }}>
                        <Box sx={{ width: '60%', height: 3, borderRadius: 0.5, bgcolor: divider, mb: 0.5 }} />
                        <Box sx={{ width: '40%', height: 2, borderRadius: 0.5, bgcolor: divider }} />
                    </Box>
                </>
            )}
        </Box>
    );
};

/**
 * Layout switcher. Vertical and Mini are wired through to the
 * Layout.jsx → SideBar `open` state via SettingsContext.themeLayout.
 *
 * Horizontal is intentionally disabled in this sprint — it needs a
 * separate <HorizontalNav> component and a layout container rewrite
 * which were scoped out of P2-4a.
 */
const LayoutOptions = () => {
    const theme = useTheme();
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const { themeLayout, onChangeLayout } = useSettingsContext();

    const surface = theme.palette.background.paper || '#FFFFFF';

    return (
        <Box>
            <SectionLabel>{isArabic ? 'الشكل' : 'Layout'}</SectionLabel>
            <Stack direction="row" spacing={1.25}>
                {OPTIONS.map((opt) => {
                    const selected = themeLayout === opt.value;
                    const card = (
                        <ButtonBase
                            key={opt.value}
                            onClick={() => !opt.disabled && onChangeLayout(opt.value)}
                            disabled={opt.disabled}
                            sx={{
                                flex: 1,
                                flexDirection: 'column',
                                gap: 0.75,
                                p: 1.25,
                                borderRadius: 2,
                                border: `1.5px solid ${
                                    selected ? theme.palette.primary.main : theme.palette.divider
                                }`,
                                bgcolor: selected
                                    ? `${theme.palette.primary.main}12`
                                    : 'transparent',
                                color: selected ? theme.palette.primary.main : 'text.primary',
                                opacity: opt.disabled ? 0.45 : 1,
                                cursor: opt.disabled ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': opt.disabled
                                    ? {}
                                    : {
                                        borderColor: theme.palette.primary.main,
                                        bgcolor: `${theme.palette.primary.main}08`,
                                    },
                            }}
                        >
                            <LayoutPreview
                                option={opt}
                                primary={theme.palette.primary.main}
                                divider={theme.palette.divider}
                                surface={surface}
                            />
                            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                                {isArabic ? opt.ar : opt.en}
                            </Typography>
                        </ButtonBase>
                    );

                    if (opt.disabled) {
                        return (
                            <Tooltip
                                key={opt.value}
                                title={isArabic ? 'قريباً' : 'Coming soon'}
                                arrow
                                placement="top"
                            >
                                {/* Tooltip needs a non-disabled wrapper to receive events */}
                                <Box sx={{ flex: 1, display: 'flex' }}>{card}</Box>
                            </Tooltip>
                        );
                    }
                    return card;
                })}
            </Stack>
        </Box>
    );
};

export default LayoutOptions;
